import { useCallback, useMemo } from 'react';
import {
  useAzureConnectorGroups,
  useBuiltInConnectorsQuery,
} from '@/hooks/useOperations';
import type { Connector } from '@/services/operationsService';
import {
  isManagedApiConnector,
  isServiceProviderConnector,
} from '@/utils/connections';
import { isAppConnectionConnectorVisible } from './connectionVisibility';
import type {
  CatalogCreationFamily,
  ConnectorChoice,
} from './addConnectionTypes';

const toConnectorChoice = (
  connector: Connector,
  family: CatalogCreationFamily,
): ConnectorChoice => ({
  id: connector.id,
  name:
    connector.properties.displayName ||
    connector.properties.generalInformation?.displayName ||
    connector.name,
  ...(connector.properties.description
    ? { description: connector.properties.description }
    : {}),
  ...(connector.properties.iconUri
    ? { icon: connector.properties.iconUri }
    : {}),
  family,
});

export const useConnectionCatalog = (
  search: string,
  projectName: string,
  appName: string,
) => {
  const managedQuery = useAzureConnectorGroups(projectName);
  const builtInQuery = useBuiltInConnectorsQuery(projectName, appName);

  const managedChoices = useMemo<ConnectorChoice[]>(
    () =>
      (managedQuery.data ?? [])
        .filter(
          (connector) =>
            isManagedApiConnector(connector.id) &&
            isAppConnectionConnectorVisible(connector.id),
        )
        .map((connector) => toConnectorChoice(connector, 'managed'))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [managedQuery.data],
  );

  const builtInChoices = useMemo<ConnectorChoice[]>(() => {
    const byId = new Map<string, ConnectorChoice>();
    for (const connector of builtInQuery.data ?? []) {
      if (!isServiceProviderConnector(connector.id)) continue;
      const key = connector.id.toLowerCase();
      if (!byId.has(key)) {
        byId.set(key, toConnectorChoice(connector, 'serviceProvider'));
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [builtInQuery.data]);

  const connectors = useMemo(() => {
    const choices = [...builtInChoices, ...managedChoices];
    const query = search.trim().toLowerCase();
    if (!query) return choices;
    return choices.filter(
      (choice) =>
        choice.name.toLowerCase().includes(query) ||
        choice.description?.toLowerCase().includes(query) ||
        choice.id.toLowerCase().includes(query),
    );
  }, [builtInChoices, managedChoices, search]);

  const retry = useCallback(() => {
    void builtInQuery.refetch();
    void managedQuery.refetch();
  }, [builtInQuery, managedQuery]);

  const sourceError = builtInQuery.error ?? managedQuery.error;

  return {
    connectors,
    isLoading:
      connectors.length === 0 &&
      (builtInQuery.isLoading || managedQuery.isLoading),
    error: connectors.length === 0 ? sourceError : null,
    partialError: connectors.length > 0 ? sourceError : null,
    retry,
  };
};
